/**
 * Push Notification Service — manages permissions, token sync, and in-app handling.
 *
 * Capacitor plugin packages are installed in the frontend so they're
 * bundled with the React app (required for remote URL loading via server.url).
 */
import axios from 'axios';
import { PushNotifications } from '@capacitor/push-notifications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PERM_KEY = 'push_permission_state';
const TOKEN_KEY = 'fcm_device_token';
const ONBOARDING_KEY = 'push_onboarding_shown';

let listenersRegistered = false;

// Must mirror backend/notifications.py's NOTIFICATION_SOUNDS android_channel_id
// values exactly — the backend sends a channel_id and relies on the channel
// already existing on-device with the right sound (Android fixes a channel's
// sound at creation time; it can't be changed per-message afterwards).
const ORDER_SOUND_CHANNELS = [
  { id: 'orders_default', name: 'Orders (Default sound)' },
  { id: 'orders_chime', name: 'Orders (Chime)', sound: 'chime.wav' },
  { id: 'orders_bell', name: 'Orders (Bell)', sound: 'bell.wav' },
];

/**
 * Creates the fixed set of Android notification channels used for order
 * alerts, one per selectable sound. No-op on iOS (channels are an
 * Android-only concept) and safe to call repeatedly — Capacitor/Android
 * treats re-creating a channel with the same id as a no-op.
 */
export async function ensureNotificationChannels() {
  if (!isNativePlatform() || window.Capacitor?.getPlatform?.() !== 'android') return;
  try {
    for (const channel of ORDER_SOUND_CHANNELS) {
      await PushNotifications.createChannel({
        id: channel.id,
        name: channel.name,
        description: 'New order alerts',
        importance: 4, // HIGH — sound + heads-up popup
        visibility: 1,
        ...(channel.sound ? { sound: channel.sound } : {}),
      });
    }
  } catch (e) {
    console.error('[Push] Channel setup failed:', e.message);
  }
}

// Firebase occasionally rotates a device's token (reinstall, restore, periodic
// rotation). The backend upserts fcm_tokens keyed by token value, so an old
// token left registered would sit alongside the new one and both would
// receive pushes — a duplicate-notification bug. Best-effort deregister the
// old value (without touching localStorage/window state, since the caller is
// about to overwrite those with the new token immediately after).
async function unregisterSpecificToken(oldToken) {
  if (!oldToken) return;
  const authToken = localStorage.getItem('auth_token') || localStorage.getItem('token');
  if (!authToken) return;
  try {
    await axios.delete(`${API}/push/fcm/token/${encodeURIComponent(oldToken)}`, {
      headers: { Authorization: `Bearer ${authToken}` }
    });
  } catch (e) {
    // Best-effort — a stale token left behind will still self-clean on its next failed send.
  }
}

// A raw APNs device token, hex-encoded by Capacitor: 64 chars on devices,
// longer on the Simulator, and Apple doesn't guarantee the length. FCM tokens
// always contain non-hex characters (e.g. ':'), so all-hex means APNs. FCM
// cannot send to it — it must never be registered with the backend.
export const isApnsDeviceToken = (value) => /^[0-9a-f]{64,}$/i.test(value || '');

// Remember this device's FCM token and register it with the backend,
// replacing the previous token if Firebase rotated it.
async function adoptDeviceToken(token) {
  if (!token) return;
  const existing = localStorage.getItem(TOKEN_KEY);
  if (existing && existing !== token) await unregisterSpecificToken(existing);
  localStorage.setItem(TOKEN_KEY, token);
  window.__fcmToken = token;
  await syncTokenToBackend(token);
}

// ── Legacy iOS FCM token receiver ────────────────────────────────
// iOS apps built before the AppDelegate passed the FCM token through
// Capacitor report only the APNs token via the 'registration' event; their
// AppDelegate injects the FCM token here with evaluateJavaScript instead.
// Kept so those installed builds keep registering until they're updated.
window.__onFCMToken = async (token) => {
  if (!token) return;
  console.log('[Push] FCM Token from native Firebase (legacy iOS build):', token.substring(0, 30) + '...');
  await adoptDeviceToken(token);
};

export function isNativePlatform() {
  return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
}

export function getPermissionState() {
  return localStorage.getItem(PERM_KEY);
}

export function hasShownOnboarding() {
  return localStorage.getItem(ONBOARDING_KEY) === 'true';
}

export function markOnboardingShown() {
  localStorage.setItem(ONBOARDING_KEY, 'true');
}

export function skipPermission() {
  localStorage.setItem(PERM_KEY, 'skipped');
  markOnboardingShown();
}

export async function checkPermissionStatus() {
  if (!isNativePlatform()) return 'unsupported';
  try {
    const result = await PushNotifications.checkPermissions();
    return result.receive;
  } catch {
    return 'unsupported';
  }
}

/**
 * Set up all push notification listeners.
 * MUST be called BEFORE PushNotifications.register() to avoid missing the token event.
 */
export async function initPushListeners(onNotification) {
  if (!isNativePlatform() || listenersRegistered) return;
  listenersRegistered = true;

  // Must happen before any notification can arrive — Android channels fix
  // their sound at creation time, so this can't be deferred to first-receive.
  await ensureNotificationChannels();

  try {
    // Token received. Android: the FCM token. iOS: also the FCM token — the
    // AppDelegate hands APNs' device token to Firebase and reports the
    // resulting FCM token through Capacitor (and again whenever it rotates).
    await PushNotifications.addListener('registration', async (token) => {
      const platform = window.Capacitor?.getPlatform?.();
      console.log(`[Push] Token from Capacitor (${platform}):`, token.value?.substring(0, 30) + '...');

      if (platform === 'ios' && isApnsDeviceToken(token.value)) {
        // Older iOS build: this is the raw APNs token; its FCM token arrives
        // separately via window.__onFCMToken (or already did).
        if (window.__nativeFCMToken) await adoptDeviceToken(window.__nativeFCMToken);
        else console.log('[Push] iOS: APNs token received, waiting for FCM token from Firebase native...');
        return;
      }
      await adoptDeviceToken(token.value);
    });

    // Registration error
    await PushNotifications.addListener('registrationError', (error) => {
      console.error('[Push] Registration error:', JSON.stringify(error));
    });

    // Foreground notification
    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('[Push] Foreground notification:', notification.title);
      if (onNotification) {
        onNotification({ title: notification.title, body: notification.body, data: notification.data, foreground: true });
      }
    });

    // Notification tapped (background/terminated)
    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('[Push] Notification tapped:', action.notification.title);
      const data = action.notification.data;
      if (data?.route) window.location.href = data.route;
      if (onNotification) {
        onNotification({ title: action.notification.title, body: action.notification.body, data, foreground: false, tapped: true });
      }
    });

    console.log('[Push] Listeners registered');
  } catch (e) {
    console.error('[Push] Listener init failed:', e);
  }
}

/**
 * Request permission → set up listeners → register for FCM token.
 * Order matters: listeners MUST be ready before register() fires the token event.
 */
export async function requestPermission(onNotification) {
  if (!isNativePlatform()) {
    if ('Notification' in window) {
      const result = await Notification.requestPermission();
      localStorage.setItem(PERM_KEY, result);
      return result;
    }
    return 'unsupported';
  }

  try {
    const permResult = await PushNotifications.requestPermissions();
    const state = permResult.receive;
    localStorage.setItem(PERM_KEY, state);
    markOnboardingShown();

    if (state === 'granted') {
      // 1. Set up listeners FIRST (so we catch the token event)
      await initPushListeners(onNotification);
      // 2. THEN register (triggers the 'registration' event with FCM token)
      try {
        await PushNotifications.register();
        console.log('[Push] Registered for push notifications');
      } catch (regErr) {
        // Firebase not configured (missing google-services.json / GoogleService-Info.plist)
        console.error('[Push] Registration failed (Firebase may not be configured):', regErr.message || regErr);
        console.error('[Push] The app will work normally, but push notifications are disabled.');
        return state; // Don't crash — app still works
      }

      // 3. Diagnostic: warn if no token received after 15s
      setTimeout(() => {
        if (!localStorage.getItem(TOKEN_KEY) && !window.__fcmToken) {
          const platform = window.Capacitor?.getPlatform?.();
          if (platform === 'ios') {
            console.error('[Push] WARNING: No FCM token received after 15s (iOS). Possible causes:');
            console.error('[Push]   1. APNs Auth Key (.p8) not uploaded to Firebase Console');
            console.error('[Push]      → Firebase Console → Project Settings → Cloud Messaging → Apple app configuration');
            console.error('[Push]   2. GoogleService-Info.plist not matching the Firebase project');
            console.error('[Push]   3. Push Notifications capability (aps-environment) missing, or');
            console.error('[Push]      AppDelegate not forwarding the FCM token — rebuild app with latest build_app.py');
          } else {
            console.error('[Push] WARNING: No FCM token received after 15s');
            console.error('[Push]   Check google-services.json is in the app bundle');
          }
        }
      }, 15000);
    }
    return state;
  } catch (e) {
    console.error('[Push] Permission request failed:', e);
    return 'error';
  }
}

/**
 * Sync FCM token to backend
 */
export async function syncTokenToBackend(token) {
  if (!token) {
    token = localStorage.getItem(TOKEN_KEY) || window.__fcmToken;
  }
  if (!token) {
    console.warn('[Push] No token to sync');
    return false;
  }

  const authToken = localStorage.getItem('auth_token') || localStorage.getItem('token');
  if (!authToken) {
    console.warn('[Push] No auth token — cannot sync FCM token to backend');
    return false;
  }

  try {
    const platform = window.Capacitor?.getPlatform?.() || 'web';
    await axios.post(`${API}/push/fcm/token`, {
      token: token,
      device_info: {
        platform: platform,
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      }
    }, {
      headers: { Authorization: `Bearer ${authToken}` }
    });
    console.log('[Push] Token synced to backend successfully');
    return true;
  } catch (e) {
    console.error('[Push] Token sync failed:', e.response?.status, e.message);
    return false;
  }
}

/**
 * On logout: stop this device receiving the account's notifications.
 * The FCM token itself is kept — it identifies this app install, not the
 * account — so the next login on this device re-registers it (App.js syncs
 * on login). Clearing it here left the device with no pushes after logging
 * back in until the app was restarted.
 */
export async function unregisterToken(authTokenOverride) {
  const token = localStorage.getItem(TOKEN_KEY) || window.__fcmToken;
  const authToken = authTokenOverride || localStorage.getItem('auth_token') || localStorage.getItem('token');
  if (!token || !authToken) return;

  try {
    await axios.delete(`${API}/push/fcm/token/${encodeURIComponent(token)}`, {
      headers: { Authorization: `Bearer ${authToken}` }
    });
    console.log('[Push] Token unregistered for this account');
  } catch (e) {
    console.error('[Push] Unregister failed:', e.message);
  }
}

export function shouldShowOnboarding() {
  if (!isNativePlatform()) return false;
  if (hasShownOnboarding()) return false;
  if (getPermissionState() === 'granted') return false;
  return true;
}
